import { Grid } from '@mui/material';
import Box from '@mui/material/Box';
import IframeResizer from 'iframe-resizer-react';
import { get as _get } from 'lodash';
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../app/hooks/hooks';
import { getClaimNumber, getServicingOpCo, getSessionId, getUrlKey } from '../../../app/store/combinedSelectors';
import * as ContextRouterActions from '../../../app/store/ContextRouter/ContextRouterActions';
import { updateStageState } from '../../../app/store/EligibilityService/EligibilityServiceSlice';
import { useActions } from '../../../app/store/useActions';
import { opCoThemeIdMapper } from '../../../themes/opCoThemeIdMapper';
import { FailedPhotoItem, ProviderInfo, StageNames } from '../../../types/type';
import { trustedOrigins } from '../../../utils/Utilities';
import ModalLinearActivityIndicator from '../../shared/ModalLinearActivityIndicator/ModalLinearActivityIndicator';
import StepProgressBar from '../../shared/StepProgressBar/StepProgressBar';

interface PhotoServicesOptions {
  isStandalone: boolean;
  cvpRefNum: string;
  insuredLastName: string;
  authKey: string;
}

export default function PhotoServicesSelection() {
  const { state } = useLocation();
  const [modalLoadingOpen, setModalLoadingOpen] = React.useState(false);
  const [url, setUrl] = React.useState('');

  const { isStandalone, cvpRefNum, insuredLastName, authKey } = state as PhotoServicesOptions;

  const contextRouterActions: typeof ContextRouterActions = useActions(ContextRouterActions);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const urlKey = useSelector(getUrlKey);
  const sessionId = useSelector(getSessionId);
  const opCo = useSelector(getServicingOpCo);
  const availableProgressStages = useAppSelector((state) => state.services.availableProgressStages);
  const currentStage = availableProgressStages.filter((stage) => stage.viewActive === true);
  const claimNumber = useSelector(getClaimNumber);

  useEffect(() => {
    let destinationURL = '';
    if (cvpRefNum) {
      destinationURL =
        process.env['NX_DCV2_UI_PHOTO_UPLOAD_APP_BASE_URL'] +
        '?theme=' +
        opCoThemeIdMapper(opCo) +
        '&refNum=' +
        cvpRefNum +
        '&lastName=' +
        insuredLastName +
        '&urlKey=' +
        urlKey +
        '&sessionId=' +
        sessionId +
        '&custKey=' +
        authKey +
        '&claimNumber=' +
        claimNumber;
    } else {
      destinationURL =
        process.env['NX_DCV2_UI_PHOTO_UPLOAD_APP_BASE_URL'] +
        '?theme=' +
        opCoThemeIdMapper(opCo) +
        '&urlKey=' +
        urlKey +
        '&sessionId=' +
        sessionId +
        '&custKey=' +
        authKey +
        '&claimNumber=' +
        claimNumber;
    }
    setUrl(destinationURL);
    console.log('Photo upload destination URL is set');
  }, [authKey, claimNumber, cvpRefNum, insuredLastName, opCo, urlKey]);

  useEffect(() => {
    const handleMessage = async (event: MessageEvent) => {
      if (trustedOrigins.includes(event.origin)) {
        const code = _get(event, 'data.messageName', 'NO_MESSAGE_FOUND');
        switch (code) {
          case 'POST_FNOL_GO_TO_CLAIM':
            goToClaimsOverview();
            break;
          case 'PHOTO_UPLOAD_SUCCESS':
            {
              const numPhotosUploaded = _get(event, 'data.uploadedPhotoCnt', '').toString();
              const failedPhotoItems = _get(event, 'data.failedItems', []);
              handleNext(numPhotosUploaded, failedPhotoItems);
            }
            break;
          case 'PHOTO_LOAD_COMPLETE':
            setModalLoadingOpen(false);
            break;
          case 'POST_FNOL_UPDATE_CUSTKEY':
            contextRouterActions.updateAuthToken(_get(event, 'data.custKey', ''));
            break;
          case 'PHOTO_UPLOAD_SPINNER_STOP':
            setModalLoadingOpen(false);
            break;
          case 'PHOTO_UPLOAD_FAILED_WITH_ERROR':
            setModalLoadingOpen(false);
            // TODO: Set error - show user-facing error message
            break;
        }
      }
    };

    window.addEventListener('message', handleMessage, false);

    return () => {
      window.removeEventListener('message', handleMessage, false);
    };
  }, [contextRouterActions, navigate, dispatch, availableProgressStages, currentStage, isStandalone]);

  async function goToClaimsOverview() {
    navigate('/claims-overview', {
      state: {
        isClaimRefreshNeeded: true,
      },
    });
  }

  async function handleNext(numPhotosUploaded: string, failedPhotoItems: FailedPhotoItem[] = []) {
    //Update the current stage state and prepare the next stage state.
    if (!isStandalone) {
      const updatedStages = availableProgressStages.map((stage) => {
        stage =
          stage.id === currentStage[0].id
            ? {
                ...stage,
                completed: true,
                viewActive: true,
                showProgress: false,
                skipped: false,
              }
            : stage;
        return stage;
      });
      dispatch(updateStageState({ updatedStages: updatedStages }));
    }

    // Set Provider Info
    const provider: ProviderInfo = {
      serviceTypeName: StageNames.PHOTOS,
      serviceAvailable: 'available',
      providerName: '',
      providerPhone: '',
      providerAddress: '',
      numberUploaded: numPhotosUploaded,
      failedPhotoItems: failedPhotoItems,
    };

    // Go to the next screen
    navigate('/auto-service-confirmation', {
      state: {
        provider: provider,
        isStandalone: isStandalone,
      },
    });
  }

  const servicesContent = (
    <>
      {!isStandalone && <StepProgressBar stateInfo={availableProgressStages}></StepProgressBar>}
      <Grid
        container
        sx={{
          width: '100%',
          maxWidth: '1280px',
          minWidth: '310px',
          margin: '0 auto',
        }}
      >
        <Grid item xs={12}>
          <IframeResizer
            src={url}
            style={{
              width: '1px',
              minWidth: '100%',
              minHeight: '48em',
              borderWidth: '0px',
            }}
            heightCalculationMethod="taggedElement"
            scrolling={false}
            checkOrigin={trustedOrigins}
          />
        </Grid>
      </Grid>
      {modalLoadingOpen && <ModalLinearActivityIndicator />}
    </>
  );

  return <Box sx={{ width: '100%' }}>{servicesContent}</Box>;
}
